import { Employee, EmployeePosition, CreateEmployeeData, UpdateEmployeeData } from '../types/employee.types';

// Base URL de la API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Función para obtener el token de autenticación
const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

// Función para hacer peticiones autenticadas
const apiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const employeeService = {
  // Obtener todos los empleados
  getEmployees: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    position?: string;
  }): Promise<{ data: Employee[]; total: number; pages: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
    if (params?.position && params.position !== 'all') queryParams.append('position', params.position);

    const query = queryParams.toString();
    const endpoint = `/employees${query ? `?${query}` : ''}`;

    return await apiRequest(endpoint);
  },

  // Obtener un empleado por ID
  getEmployee: async (id: string): Promise<Employee> => {
    const response = await apiRequest(`/employees/${id}`);
    return response.data;
  },

  // Crear nuevo empleado
  createEmployee: async (employeeData: CreateEmployeeData): Promise<Employee> => {
    const response = await apiRequest('/employees', {
      method: 'POST',
      body: JSON.stringify(employeeData),
    });
    return response.data;
  },

  // Actualizar empleado
  updateEmployee: async (id: string, employeeData: UpdateEmployeeData): Promise<Employee> => {
    const response = await apiRequest(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(employeeData),
    });
    return response.data;
  },

  // Eliminar empleado (soft delete)
  deleteEmployee: async (id: string): Promise<void> => {
    await apiRequest(`/employees/${id}`, {
      method: 'DELETE',
    });
  },

  // Obtener posiciones disponibles
  getPositions: async (): Promise<{ data: EmployeePosition[] }> => {
    return await apiRequest('/employees/positions');
  },

  // Cambiar estado de empleado
  toggleEmployeeStatus: async (id: string): Promise<Employee> => {
    const response = await apiRequest(`/employees/${id}/toggle-status`, {
      method: 'PATCH',
    });
    return response.data;
  }
};